import React, { ComponentPropsWithoutRef, useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Divider, Stack, SxProps, Theme, useTheme } from '@mui/material';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { useScrollBoundary } from '@occc/utils';

import { MetricCard, MetricCardProps } from '../metric-card';

const containerSx: SxProps<Theme> = {
  '--fade-left-length': '80px',
  '--fade-right-length': '80px',
  '--fade-left-intensity': 1,
  '--fade-right-intensity': 1,

  width: '100%',
  backgroundColor: (theme) => theme.palette.grey[200],
  display: 'flex',
  overflowX: 'auto',
  pt: 1,
  scrollbarWidth: 'none',
  scrollSnapType: 'x mandatory',
  '&::-webkit-scrollbar': { display: 'none' },

  maskImage:
    'linear-gradient(to right, rgba(255, 255, 255, calc(1 - var(--fade-left-intensity, 1))) 0, white var(--fade-left-length, 0px), white calc(100% - var(--fade-right-length, 0px)), rgba(255, 255, 255, calc(1 - var(--fade-right-intensity, 1))) 100%)',
  maskComposite: 'intersect',
  maskRepeat: 'no-repeat',
  maskSize: '100% 100% 100% 100%',

  '&[data-at-start="true"]': {
    '--fade-left-intensity': 0,
  },
  '&[data-at-end="true"]': {
    '--fade-right-intensity': 0,
  },
};

const dataAttr = (condition: boolean | undefined) => (condition ? true : undefined);

const Container = (props: Omit<ComponentPropsWithoutRef<typeof Box>, 'sx'>) => {
  const containerRef = useRef<HTMLElement | null>(null);
  const scrollState = useScrollBoundary(containerRef, { axis: 'x' });

  return (
    <Box
      ref={containerRef}
      data-at-start={dataAttr(scrollState.isAtStart)}
      data-at-end={dataAttr(scrollState.isAtEnd)}
      sx={containerSx}
      {...props}
    />
  );
};

const defaultItemStyle: SxProps<Theme> = {
  p: 2,
};

export type MetricTabsItem = Omit<
  MetricCardProps,
  'onClick' | 'sx' | 'isLabelNotWrapped' | 'isNoHoverEffect'
>;

export type MetricTabsProps = {
  items: MetricTabsItem[];
  index?: number;
  onChange: (index: number, item: MetricTabsItem) => void;
  children?: React.ReactNode;
};

export const MetricTabs = ({ items, index = 0, onChange, children }: MetricTabsProps) => {
  const theme = useTheme();

  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const hasInitialScroll = useRef(false);

  useEffect(() => {
    if (hasInitialScroll.current) return;
    hasInitialScroll.current = true;
    const el = itemRefs.current[index];
    if (el) {
      el.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
    }
  }, [index]);

  const click = useCallback(
    (item: MetricCardProps) => {
      const index = items.findIndex(
        (v) => v.label === item.label && v.value === item.value && v.payload === item.payload,
      );
      if (index < 0) {
        return;
      }
      onChange(index, items[index]);
    },
    [items, onChange],
  );

  const selectedItemStyle = useMemo(
    () => ({
      ...defaultItemStyle,
      backgroundColor: theme.palette.common.white,
      borderTopLeftRadius: theme.spacing(1),
      borderTopRightRadius: theme.spacing(1),
    }),
    [theme],
  );

  if (!items.length) return null;

  return (
    <Stack>
      <Container>
        {items.map((item, i) => {
          const isSelected = i === index;
          const isNextSelected = i + 1 === index;
          const isLast = i === items.length - 1;
          return (
            <Stack
              direction='row'
              position='relative'
              key={`item-${item.payload}-${item.label}-${item.value}`}
            >
              <Box
                pl={1}
                sx={{ scrollSnapAlign: 'center' }}
                display='flex'
                flexDirection='row'
                ref={(el: HTMLDivElement | null) => (itemRefs.current[i] = el)}
              >
                <Box sx={isSelected ? selectedItemStyle : defaultItemStyle}>
                  <MetricCard
                    {...item}
                    onClick={click}
                    sx={{ minWidth: theme.spacing(25), '& > *': { width: '100%' } }}
                    isLabelNotWrapped
                    isNoHoverEffect
                  />
                </Box>
                {isSelected || isNextSelected || isLast ? null : (
                  <Divider
                    orientation='vertical'
                    sx={{
                      position: 'absolute',
                      right: 0,
                      alignSelf: 'center',
                      height: '60%',
                    }}
                  />
                )}
              </Box>
            </Stack>
          );
        })}
      </Container>
      {children}
    </Stack>
  );
};
